import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SignupWithEmailPassword.css';
import TopBar from '../../common/TopBar/TopBar';
import AuthInput from '../../buttons/AuthInput';
import CTAButton from '../../buttons/CTAButton';
import { emailSignup, checkEmailDuplicate } from '../../../api/auth';
import VIToast from '../../common/VIToast/VIToast';
import VIModal from '../../common/VIModal/VIModal';
import { AxiosError } from 'axios';

const SignupWithEmailPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(true);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  useEffect(() => {
    if (emailTouched) {
      setIsEmailValid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));
    }
  }, [email, emailTouched]);

  useEffect(() => {
    if (passwordTouched) {
      setIsPasswordValid(password.length >= 8);
    }
  }, [password, passwordTouched]);

  useEffect(() => {
    if (confirmPasswordTouched) {
      setIsConfirmPasswordValid(password === confirmPassword);
    }
  }, [password, confirmPassword, confirmPasswordTouched]);

  const isFormValid = emailTouched && passwordTouched && confirmPasswordTouched && isEmailValid && isPasswordValid && isConfirmPasswordValid;

  const handleNext = async () => {
    if (isFormValid && !isSigningUp) {
      setIsSigningUp(true);
      try {
        const isDuplicate = await checkEmailDuplicate(email);
        if (isDuplicate) {
          setShowToast(true);
        } else {
          navigate('/signup/profile', { 
            state: { email, password },
            replace: true 
          });
        }
      } catch (error) {
        console.error('Email check failed:', error);
        setApiError("This email is already registered");
        setShowErrorModal(true);
      } finally {
        setIsSigningUp(false);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPasswords(!showPasswords);
  };

  const closeErrorModal = () => {
    setShowErrorModal(false);
    setApiError(null);
  };

  return (
    <div className="signup-email-password">
      {showToast && (
        <VIToast
          message="This email is already registered"
          onClose={() => setShowToast(false)}
        />
      )}
      
      {showErrorModal && apiError && (
        <VIModal
          title="Error"
          onClose={closeErrorModal}
          isOpen={showErrorModal}
        >
          <div className="error-modal-content">
            <p>{apiError}</p>
          </div>
        </VIModal>
      )}
      
      <TopBar showBackButton={true} />
      <h1 className="title">Join Vivid</h1>
      <p className="description">
        You'll receive store credits and other benefits with this email address!
      </p>
      <div className="input-container">
        <AuthInput
          type="text"
          placeholder="Write email"
          value={email}
          onChange={(value: string) => {
            setEmail(value);
            setEmailTouched(true);
            setIsDuplicateEmail(false);
          }}
          isValid={isEmailValid}
        />
        <AuthInput
          type="password"
          placeholder="Write password"
          value={password}
          onChange={(value: string) => {
            setPassword(value);
            setPasswordTouched(true);
          }}
          isValid={isPasswordValid}
          showPassword={showPasswords}
          onTogglePassword={togglePasswordVisibility}
        />
        <AuthInput
          type="password"
          placeholder="Confirm password"
          value={confirmPassword}
          onChange={(value: string) => {
            setConfirmPassword(value);
            setConfirmPasswordTouched(true);
          }}
          isValid={isConfirmPasswordValid}
          showPassword={showPasswords}
          onTogglePassword={togglePasswordVisibility}
        />
      </div>
      <CTAButton
        text={isSigningUp ? "Please wait..." : "Next"}
        onClick={handleNext}
        isActive={isFormValid && !isSigningUp}
      />
    </div>
  );
};

export default SignupWithEmailPassword;
